/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';

import { Mode } from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { ApprovalTemplateInstanceBasicView } from '../../../service/gen';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ScrollBoundary } from '../../common';

import {
    TemplateApprovalPreviewComponentProps,
    TemplatedApprovalPreview,
} from '../../containers/CreateTemplateApproval/TemplatedApprovalPreview.component';
import { ErrorPage, ErrorPageProps } from '../../containers/ErrorPage';
import { LoadingPage } from '../../containers/LoadingPage';
import { TemplateModalHeader } from '../TemplateModalHeader';
import { getBodyStyles, getFooterStyles, getHeaderStyles, getPageStyles } from './DeployTemplate.styles';

export interface DeployTemplateComponentProps {
    onNext?: () => void;
    onCancel?: () => void;
    iconName: string;
    iconBackgroundColor: string;
    title: string;
    description: string;
    isLoading: boolean;
    mode: Mode;
    errorProps?: ErrorPageProps;
    templatePreviewProps: TemplateApprovalPreviewComponentProps;
}

export const DeployTemplateComponent: React.FC<DeployTemplateComponentProps> = (
    props: DeployTemplateComponentProps
): JSX.Element => {
    const loc = getAppHost().getLocale();

    const scrollRef = React.useRef<HTMLDivElement>(null);

    const {
        onCancel,
        onNext,
        iconName,
        iconBackgroundColor,
        title,
        description,
        isLoading,
        mode,
        errorProps,
        templatePreviewProps,
    } = props;

    const iconProps: ApprovalTemplateInstanceBasicView = {
        IconName: iconName,
        AccentColor: iconBackgroundColor,
        Title: title,
        Description: description,
        Scope: null,
        Category: null,
        FormId: null,
    };

    let actionText = loc.strings.publish_button_text;
    let loadingText = loc.strings.publishing_template_text;
    if (mode === Mode.Edit) {
        actionText = loc.strings.save_button_text;
        loadingText = loc.strings.saving_template_text;
    }

    return (
        <Stack styles={getPageStyles}>
            {isLoading ? (
                <LoadingPage message={loadingText} />
            ) : errorProps ? (
                <ErrorPage {...errorProps} />
            ) : (
                <>
                    <Stack.Item styles={getHeaderStyles()}>
                        <TemplateModalHeader templateInstance={iconProps} teamName={loc.strings.preview} />
                    </Stack.Item>

                    <div ref={scrollRef} style={getBodyStyles()}>
                        <ScrollBoundary isTopBoundary={true} />
                        <TemplatedApprovalPreview {...templatePreviewProps} scrollRef={scrollRef} />
                        <ScrollBoundary isTopBoundary={false} />
                    </div>

                    <Stack.Item>
                        <Stack horizontal verticalAlign='center' styles={getFooterStyles}>
                            <Stack.Item>
                                <PrimaryButton aria-label={actionText} text={actionText} onClick={onNext} />
                            </Stack.Item>
                            <Stack.Item>
                                <DefaultButton
                                    aria-label={loc.strings.action_cancel}
                                    text={loc.strings.action_cancel}
                                    onClick={onCancel}
                                />
                            </Stack.Item>
                        </Stack>
                    </Stack.Item>
                </>
            )}
        </Stack>
    );
};
